import { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaCalendarAlt, FaTag } from 'react-icons/fa';
import LoadingSpinner from '../LoadingSpinner';
import '../../styles/admin/PromotionsManagement.css';

const PromotionsManagement = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    minimum_purchase: '',
    product_category: '',
    valid_from: '',
    valid_to: '',
    max_uses: '',
    current_uses: 0,
    description: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        setLoading(true);

        // Fetch promotions
        const response = await fetch('/api/admin/promotions', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch promotions');
        }
        const data = await response.json();
        setPromotions(data);

        // Fetch categories for the category dropdown
        const categoriesResponse = await fetch('/api/admin/categories', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (categoriesResponse.ok) {
          const categoriesData = await categoriesResponse.json();
          setCategories(categoriesData);
        }
      } catch (error) {
        // Error will be handled by the global error handler
      } finally {
        setLoading(false);
      }
    };

    fetchPromotions();
  }, []);

  const validateForm = () => {
    const errors = {};

    if (!formData.code) {
      errors.code = 'Promotion code is required';
    }
    if (!formData.discount_value) {
      errors.discount_value = 'Discount value is required';
    }
    if (
      formData.discount_type === 'percentage' &&
      (parseFloat(formData.discount_value) <= 0 || parseFloat(formData.discount_value) > 100)
    ) {
      errors.discount_value = 'Percentage discount must be between 1 and 100';
    }
    if (formData.discount_type === 'fixed' && parseFloat(formData.discount_value) <= 0) {
      errors.discount_value = 'Fixed discount must be greater than 0';
    }
    if (!formData.valid_from) {
      errors.valid_from = 'Start date is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const endpoint = editingPromotion
        ? `/api/admin/promotions/${editingPromotion.id}`
        : '/api/admin/promotions';

      const method = editingPromotion ? 'PUT' : 'POST';

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to save promotion');
      }

      const savedPromotion = await response.json();

      if (editingPromotion) {
        setPromotions(promotions.map(p => (p.id === editingPromotion.id ? savedPromotion : p)));
      } else {
        setPromotions([...promotions, savedPromotion]);
      }

      setShowForm(false);
    } catch (error) {
      // Error will be handled by the global error handler
      setFormErrors({
        submit: 'Failed to save promotion. Please try again.',
      });
    }
  };

  const handleEditPromotion = promotion => {
    setEditingPromotion(promotion);
    setFormData({
      code: promotion.code || '',
      discount_type: promotion.discount_type || 'percentage',
      discount_value: promotion.discount_value ?? '',
      minimum_purchase: promotion.minimum_purchase ?? '',
      product_category: promotion.product_category || '',
      valid_from: promotion.valid_from ? String(promotion.valid_from).slice(0, 10) : '',
      valid_to: promotion.valid_to ? String(promotion.valid_to).slice(0, 10) : '',
      max_uses: promotion.max_uses ?? '',
      current_uses: promotion.current_uses || 0,
      description: promotion.description || '',
    });
    setFormErrors({});
    setShowForm(true);
  };

  const handleDeletePromotion = async id => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/promotions/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete promotion');
      }

      setPromotions(promotions.filter(promo => promo.id !== id));
    } catch (error) {
      // Import logger to handle the error properly
      import('../../services/logger').then(({ default: logger }) => {
        logger.error('Error deleting promotion', error, {
          promotionId: id,
          component: 'PromotionsManagement',
        });
      });
      import('../../services/errorHandler').then(({ default: errorHandler }) => {
        errorHandler.handleApiError(error);
      });
      alert('Failed to delete promotion. Please try again.');
    }
  };

  const getPromotionStatus = promotion => {
    const now = new Date();
    const startDate = new Date(promotion.valid_from);
    const endDate = promotion.valid_to ? new Date(promotion.valid_to) : null;

    if (promotion.max_uses && promotion.current_uses >= promotion.max_uses) {
      return 'maxed';
    }

    if (startDate > now) {
      return 'upcoming';
    }

    if (endDate && endDate < now) {
      return 'expired';
    }

    return 'active';
  };

  const getStatusBadge = status => {
    switch (status) {
      case 'active':
        return <span className='status-badge active'>Active</span>;
      case 'upcoming':
        return <span className='status-badge upcoming'>Upcoming</span>;
      case 'expired':
        return <span className='status-badge expired'>Expired</span>;
      case 'maxed':
        return <span className='status-badge maxed'>Max Uses Reached</span>;
      default:
        return null;
    }
  };

  const getDiscountDisplay = promotion => {
    if (promotion.discount_type === 'percentage') {
      return `${promotion.discount_value}% off`;
    } else {
      return `$${parseFloat(promotion.discount_value).toFixed(2)} off`;
    }
  };

  // Filter promotions based on search and status
  const filteredPromotions = promotions.filter(promotion => {
    const matchesSearch =
      promotion.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (promotion.description &&
        promotion.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const status = getPromotionStatus(promotion);
    const matchesStatus = filterStatus === 'all' || status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className='promotions-management'>
      {showForm && (
        <div className='promotion-form-overlay'>
          <div className='promotion-form-container'>
            <div className='form-header'>
              <h2>{editingPromotion ? 'Edit Promotion' : 'Add New Promotion'}</h2>
              <button className='close-btn' onClick={() => setShowForm(false)}>
                &times;
              </button>
            </div>

            <form className='promotion-form' onSubmit={handleSubmit}>
              <div className='form-grid'>
                <div className='form-group'>
                  <label htmlFor='code'>Promotion Code *</label>
                  <input
                    type='text'
                    id='code'
                    name='code'
                    value={formData.code}
                    onChange={handleFormChange}
                    className={formErrors.code ? 'error' : ''}
                  />
                  {formErrors.code && <div className='error-message'>{formErrors.code}</div>}
                </div>

                <div className='form-group'>
                  <label htmlFor='description'>Description</label>
                  <textarea
                    id='description'
                    name='description'
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder='Describe the promotion'
                  />
                </div>

                <div className='form-row'>
                  <div className='form-group'>
                    <label htmlFor='discount_type'>Discount Type *</label>
                    <select
                      id='discount_type'
                      name='discount_type'
                      value={formData.discount_type}
                      onChange={handleFormChange}
                    >
                      <option value='percentage'>Percentage (%)</option>
                      <option value='fixed'>Fixed Amount ($)</option>
                    </select>
                  </div>

                  <div className='form-group'>
                    <label htmlFor='discount_value'>Discount Value *</label>
                    <input
                      type='number'
                      id='discount_value'
                      name='discount_value'
                      value={formData.discount_value}
                      onChange={handleFormChange}
                      min='0'
                      step={formData.discount_type === 'fixed' ? '0.01' : '1'}
                      className={formErrors.discount_value ? 'error' : ''}
                    />
                    {formErrors.discount_value && (
                      <div className='error-message'>{formErrors.discount_value}</div>
                    )}
                  </div>
                </div>

                <div className='form-row'>
                  <div className='form-group'>
                    <label htmlFor='minimum_purchase'>Minimum Purchase ($)</label>
                    <input
                      type='number'
                      id='minimum_purchase'
                      name='minimum_purchase'
                      value={formData.minimum_purchase}
                      onChange={handleFormChange}
                      min='0'
                      step='0.01'
                    />
                  </div>

                  <div className='form-group'>
                    <label htmlFor='product_category'>Applies to Category</label>
                    <select
                      id='product_category'
                      name='product_category'
                      value={formData.product_category}
                      onChange={handleFormChange}
                    >
                      <option value=''>All Categories</option>
                      {categories.map(category => (
                        <option key={category.id || category.name} value={category.name}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className='form-row'>
                  <div className='form-group'>
                    <label htmlFor='valid_from'>Valid From *</label>
                    <input
                      type='date'
                      id='valid_from'
                      name='valid_from'
                      value={formData.valid_from}
                      onChange={handleFormChange}
                      className={formErrors.valid_from ? 'error' : ''}
                    />
                    {formErrors.valid_from && (
                      <div className='error-message'>{formErrors.valid_from}</div>
                    )}
                  </div>

                  <div className='form-group'>
                    <label htmlFor='valid_to'>Valid To</label>
                    <input
                      type='date'
                      id='valid_to'
                      name='valid_to'
                      value={formData.valid_to}
                      onChange={handleFormChange}
                      min={formData.valid_from}
                    />
                  </div>
                </div>

                <div className='form-group'>
                  <label htmlFor='max_uses'>Maximum Uses</label>
                  <input
                    type='number'
                    id='max_uses'
                    name='max_uses'
                    value={formData.max_uses}
                    onChange={handleFormChange}
                    min='0'
                    placeholder='Unlimited if blank'
                  />
                </div>

                {editingPromotion && (
                  <div className='form-group'>
                    <label htmlFor='current_uses'>Current Uses</label>
                    <input
                      type='number'
                      id='current_uses'
                      name='current_uses'
                      value={formData.current_uses}
                      onChange={handleFormChange}
                      min='0'
                      readOnly
                    />
                    <p className='field-note'>This is tracked automatically</p>
                  </div>
                )}
              </div>

              {formErrors.submit && (
                <div className='error-message submit-error'>{formErrors.submit}</div>
              )}

              <div className='form-actions'>
                <button type='button' className='cancel-btn' onClick={() => setShowForm(false)}>
                  Cancel
                </button>

                <button type='submit' className='save-btn'>
                  {editingPromotion ? 'Update Promotion' : 'Add Promotion'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className='header-actions'>
        <button
          className='action-btn add-btn'
          onClick={() => {
            setEditingPromotion(null);
            setFormData({
              code: '',
              discount_type: 'percentage',
              discount_value: '',
              minimum_purchase: '',
              product_category: '',
              valid_from: new Date().toISOString().split('T')[0],
              valid_to: '',
              max_uses: '',
              current_uses: 0,
              description: '',
            });
            setFormErrors({});
            setShowForm(true);
          }}
        >
          <FaPlus /> Add New Promotion
        </button>

        <div className='filters'>
          <div className='search-box'>
            <FaSearch />
            <input
              type='text'
              placeholder='Search promotions...'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value='all'>All Status</option>
            <option value='active'>Active</option>
            <option value='upcoming'>Upcoming</option>
            <option value='expired'>Expired</option>
            <option value='maxed'>Max Uses Reached</option>
          </select>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {filteredPromotions.length === 0 ? (
            <div className='no-promotions'>
              <FaTag className='icon' />
              <p>No promotions found. Create your first promotion to boost sales!</p>
              <button
                className='add-btn'
                onClick={() => {
                  setEditingPromotion(null);
                  setFormData({
                    code: '',
                    discount_type: 'percentage',
                    discount_value: '',
                    minimum_purchase: '',
                    product_category: '',
                    valid_from: new Date().toISOString().split('T')[0],
                    valid_to: '',
                    max_uses: '',
                    current_uses: 0,
                    description: '',
                  });
                  setFormErrors({});
                  setShowForm(true);
                }}
              >
                <FaPlus /> Add New Promotion
              </button>
            </div>
          ) : (
            <div className='promotions-table-container'>
              <table className='promotions-table'>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Discount</th>
                    <th>Dates</th>
                    <th>Usage</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPromotions.map(promotion => {
                    const status = getPromotionStatus(promotion);

                    return (
                      <tr key={promotion.id} className={`status-${status}`}>
                        <td className='promo-code'>
                          <strong>{promotion.code}</strong>
                          {promotion.description && (
                            <p className='description'>{promotion.description}</p>
                          )}
                        </td>
                        <td>
                          <div className='discount-info'>
                            <span className='discount-value'>{getDiscountDisplay(promotion)}</span>
                            {promotion.minimum_purchase && (
                              <span className='minimum-purchase'>
                                Min: ${parseFloat(promotion.minimum_purchase).toFixed(2)}
                              </span>
                            )}
                            {promotion.product_category && (
                              <span className='category-limit'>
                                Category: {promotion.product_category}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className='dates'>
                            <div className='date-item'>
                              <FaCalendarAlt /> From:{' '}
                              {new Date(promotion.valid_from).toLocaleDateString()}
                            </div>
                            {promotion.valid_to && (
                              <div className='date-item'>
                                <FaCalendarAlt /> To:{' '}
                                {new Date(promotion.valid_to).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          {promotion.max_uses ? (
                            <span className='usage-count'>
                              {promotion.current_uses} / {promotion.max_uses} uses
                            </span>
                          ) : (
                            <span className='usage-count'>
                              {promotion.current_uses} uses (unlimited)
                            </span>
                          )}
                        </td>
                        <td>{getStatusBadge(status)}</td>
                        <td>
                          <div className='table-actions'>
                            <button
                              className='btn-edit'
                              onClick={() => handleEditPromotion(promotion)}
                              title='Edit Promotion'
                            >
                              <FaEdit />
                            </button>

                            <button
                              className='btn-delete'
                              onClick={() => handleDeletePromotion(promotion.id)}
                              title='Delete Promotion'
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PromotionsManagement;
